import { useCallback, useEffect, useState } from "react";
import {
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
  Box,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { useForm } from "react-hook-form";
import { ErrorAlert, Alert } from "../../utils";
import { TextField } from "../../utils/form";
import _ from "lodash";
import { useApi, useHealthCenterRules } from "../../utils/hooks";
import { PrescriptionAlreadyIntegratedError } from "../../apis/api";
import emailsRegex from "../../utils/emailsRegex";
import CustomDialog from "../../utils/CustomDialog";
import Promise from "bluebird";
import useFirstMedicalDocument from "./hooks/useFirstMedicalDocument";
import { useAuth } from "../../auth/context";

const phoneMethod = {
  destinationLabel: "Número de teléfono",
  validationPattern: /^(?=.*[0-9])[()0-9]+$/,
  valueGetter: ({ patient: { phone } }) => phone,
};

const emailsMethod = {
  destinationLabel: "Dirección de correo",
  validationPattern: emailsRegex,
  valueGetter: ({ patient: { email } }) => email,
};

const methodsEmails = [
  {
    value: "email",
    label: "Email del Paciente",
    helper: "Para enviar a más de un destinatario, separarlos con coma (,)",
    ...emailsMethod,
  },
];

const allWhatsappMethods = [
  {
    value: "ownWhatsapp",
    label: "Whatsapp de Recetario",
    helper: "Con código de área, sin 0 ni 15. Ej: 1140000000",
    ...phoneMethod,
  },
  {
    value: "whatsapp",
    label: "Tu Whatsapp",
    ...phoneMethod,
  }
];

const useWhatsappMethods = () => {
  const medicalDocumentsRules = useHealthCenterRules("medicalDocuments");
  if (_.isEmpty(medicalDocumentsRules.share?.whatsapp?.options)) return _.map(allWhatsappMethods, method => _.assign({}, method, { disabled: method.value === "ownWhatsapp" }));
  return _.map(allWhatsappMethods, method => _.assign({}, method, {
    disabled: !_.includes(medicalDocumentsRules.share?.whatsapp?.options, method.value)
  }));
};

const useFullMethod = (method) => {
  const whatsappMethods = useWhatsappMethods();
  return _.find(
    method === "email" ? methodsEmails : whatsappMethods,
    { value: method }
  );
};

//TODO avoid repeating typeName everywhere
const useToWhatsappShareText = medicalDocuments => {
  const medicalDocument = useFirstMedicalDocument(medicalDocuments);
  const { type } = medicalDocument;
  const typeName = type === "prescription" ? "receta" : "orden";

  const urls = _(medicalDocuments).map("url").join("\n\n");
  return `
Aquí podrá descargar su ${typeName}: 

${urls}

Recetario
`;
};

//TODO reuse MethodPicker
const ExporterOptions = ({ method, setMethod, form, medicalDocuments }) => {
  const whatsappMethods = useWhatsappMethods();
  const fullMethod = useFullMethod(method);
  const { user } = useAuth();
  const canEditShareInfo = user.showPatientShareInfo();
  
  const handleChange = (event) => {
    const newMethod = event.target.value;
    setMethod(newMethod);
  };

  const medicalDocument = useFirstMedicalDocument(medicalDocuments);
  const existingValue = fullMethod?.valueGetter?.(medicalDocument);
  const hasExistingValue = !_.isEmpty(existingValue);
  const isShareInfoLocked = !canEditShareInfo && hasExistingValue;

  useEffect(() => {
    if (fullMethod) {
      const { valueGetter } = fullMethod;
      if (!valueGetter) return;
      const value = valueGetter(medicalDocument);
      form.reset({ destination: value || "" });
    }
  }, [method]);

  return (
    <Box>
      <Typography sx={{ display: method === "email" ? "none" : "block" }}>
        Ingrese la opción deseada:
      </Typography>

      <Box>
        <RadioGroup
          aria-labelledby="demo-radio-buttons-group-label"
          name="radio-buttons-group"
          defaultValue="manual"
          row
          value={method}
          onChange={handleChange}
          sx={{ display: method === "email" ? "none" : "block" }}
        >
          {_.map(whatsappMethods, ({ value, label, disabled }) => (
            <FormControlLabel
              control={<Radio />}
              value={value}
              label={label}
              disabled={disabled}
              key={value}
            />
          ))}
        </RadioGroup>
        {!isShareInfoLocked && (
          <TextField
            sx={{ mt: 2, mb: 2 }}
            name="destination"
            required
            control={form.control}
            fullWidth
            label={fullMethod?.destinationLabel}
            rules={{
              required: true,
              pattern: { value: fullMethod?.validationPattern },
            }}
            helperText={fullMethod?.helper}
            disabled={fullMethod?.disabled}
          />
        )}
      </Box>
    </Box>
  );
};

const Exporter = (props) => {
  const {
    medicalDocuments,
    method,
    onClose,
    open,
    setMethod,
    setFailed,
    failed,
    onShared,
  } = props;
  const form = useForm({ defaultValues: { destination: "" }, shouldUnregister: false });
  const [shared, setShared] = useState(false);
  const [loading, setLoading] = useState(false);
  const medicalDocument = useFirstMedicalDocument(medicalDocuments);
  const { type } = medicalDocument;
  const api = useApi();
  const typeName = type == "prescription" ? "receta" : "orden";
  const whatsappShareText = useToWhatsappShareText(medicalDocuments);

  const shareInWhatsapp = (phoneNumber, text) => {
    const whatsappNumber = _.replace(phoneNumber, /[^0-9.]/g, "");
    window.open(
      `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(text)}`,
      "_blank",
      "noreferrer"
    );
  };

  const { handleSubmit } = form;

  const canShare = !useFullMethod(method)?.disabled;

  const onGenerate = useCallback(
    ({ destination }) => {
      switch (method) {
        case "email":
          setLoading(true);
          api.share(_.map(medicalDocuments, "id"), { method: "email", destination })
            .then(() => {
              onClose();
              setLoading(false);
              setShared(true);
            })
            .catch(() => {
              setFailed(true);
              setLoading(false);
            });

          break;

        case "whatsapp":
          shareInWhatsapp(destination, whatsappShareText);
          setLoading(true);
          const sharePromises = _.map(medicalDocuments, (doc) => 
            api.createIntegration(doc.id, { app: "whatsapp-web", integrationId: destination })
          );
          
          Promise.all(sharePromises)
            .then(() => {
              onClose();
              setLoading(false);
            })
            .catch((error) => {
              setLoading(false);

              if (error instanceof PrescriptionAlreadyIntegratedError) {
                return;
              }
              setFailed(true);
            });
          break;
        case "ownWhatsapp":
          setLoading(true);
          api.share(_.map(medicalDocuments, "id"), { method: "whatsapp", destination })
            .then(() => {
              onClose();
              setLoading(false);
              setShared(true);
              onShared?.();
            })
            .catch(() => {
              setFailed(true);
              setLoading(false);
            });

          break;
        default:
          break;
      }
    },
    [method, medicalDocuments, onShared]
  );

  return (
    <>
      <CustomDialog
        open={open}
        onClose={onClose}
        size={"xs"}
        title={`Enviar ${typeName} por ${
          method === "email" ? "Email" : "Whatsapp"
        }`}
      >
        <form
          onSubmit={handleSubmit(onGenerate)}
          autoComplete="off"
          style={{
            padding: "1rem",
            alignItems: "center",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <ExporterOptions
            method={method}
            setMethod={setMethod}
            onGenerate={onGenerate}
            form={form}
            medicalDocuments={medicalDocuments}
          />

          <LoadingButton variant="contained" type="submit" loading={loading} disabled={!canShare}>
            Enviar
          </LoadingButton>
        </form>
      </CustomDialog>
      <ErrorAlert
        show={failed}
        onClose={() => setFailed(false)}
        message="No se pudo compartir el documento. Intentá de nuevo o contactanos a consultas@recetario.com.ar"
      />
      <Alert
        severity="success"
        message="Listo! Ya compartimos el documento"
        onClose={() => setShared(false)}
        show={shared}
      />
    </>
  );
};

export default Exporter;
